/**
 * Profile Cache Utility
 *
 * This module provides a simple in-memory cache for user profiles to prevent
 * redundant Firebase calls when loading watchlist data. The cache is especially
 * useful when:
 * - Loading multiple watchlists that share the same creator/collaborators
 * - Real-time listeners trigger updates for the same users
 * - Navigating between pages that show the same user profiles
 *
 * The cache has a 5-minute TTL and automatically cleans up expired entries.
 */

import { authService, UserProfile } from '@watchlist/shared';
import { loadPersistedProfiles, persistProfile, clearPersistedProfiles, removePersistedProfile } from './profilePersistence';

interface CachedProfileEntry {
  profile: UserProfile;
  fetchedAt: number;
}

// In-memory cache with 5-minute TTL
const CACHE_TTL_MS = 5 * 60 * 1000;
const profileCache = new Map<string, CachedProfileEntry>();
const pendingFetches = new Map<string, Promise<UserProfile | null>>();

// Warm from localStorage synchronously at module load time
(function warmFromStorage() {
  const now = Date.now();
  const persisted = loadPersistedProfiles();
  for (const [userId, entry] of Object.entries(persisted)) {
    profileCache.set(userId, {
      profile: {
        uid: entry.uid,
        displayName: entry.displayName,
        username: entry.username,
        photoURL: entry.photoURL,
        photoThumbnailURL: entry.photoThumbnailURL,
        email: '',
        favoriteGenres: [],
        privacy: { profileVisibility: 'public', showWatchlist: true, showRatings: true, showActivity: true },
        stats: { moviesWatched: 0, reviewsWritten: 0, listsCreated: 0, followersCount: 0, followingCount: 0 },
      } as UserProfile,
      fetchedAt: now,
    });
  }
})();

/**
 * Get a cached profile or fetch it if not cached/expired
 * Returns the full UserProfile object for compatibility with all use cases
 */
export async function getCachedUserProfile(userId: string): Promise<UserProfile | null> {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    return null;
  }

  const now = Date.now();
  const cached = profileCache.get(userId);

  // Return cached if valid and not expired
  if (cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached.profile;
  }

  // Check if there's already a pending fetch for this user (prevent duplicate requests)
  const pendingFetch = pendingFetches.get(userId);
  if (pendingFetch) {
    return pendingFetch;
  }

  // Fetch from Firebase
  const fetchPromise = (async (): Promise<UserProfile | null> => {
    try {
      const profile = await authService.getUserProfile(userId);
      if (profile) {
        profileCache.set(userId, {
          profile,
          fetchedAt: Date.now()
        });
        persistProfile(userId, profile);
        return profile;
      }
      return null;
    } catch (error) {
      console.warn(`ProfileCache: Failed to fetch profile for ${userId}:`, error);
      return null;
    } finally {
      pendingFetches.delete(userId);
    }
  })();

  pendingFetches.set(userId, fetchPromise);
  return fetchPromise;
}

/**
 * Batch fetch multiple profiles at once
 * This is more efficient than fetching one by one because:
 * 1. It deduplicates IDs
 * 2. It fetches all uncached profiles in parallel
 * 3. It uses a single await for all fetches
 */
export async function batchFetchProfiles(userIds: string[]): Promise<void> {
  if (!userIds || userIds.length === 0) return;

  const now = Date.now();

  // Filter to only uncached/expired IDs
  const uncachedIds = userIds.filter(userId => {
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      return false;
    }
    const cached = profileCache.get(userId);
    return !cached || (now - cached.fetchedAt) >= CACHE_TTL_MS;
  });

  // Remove duplicates
  const uniqueIds = Array.from(new Set(uncachedIds));

  if (uniqueIds.length === 0) {
    return;
  }

  console.log(`📦 ProfileCache: Batch fetching ${uniqueIds.length} profiles (${userIds.length - uniqueIds.length} already cached)`);

  // Fetch all in parallel
  await Promise.all(uniqueIds.map(userId => getCachedUserProfile(userId)));

  console.log(`✅ ProfileCache: Batch fetch complete, cache size: ${profileCache.size}`);
}

/**
 * Get profile from cache synchronously (returns null if not cached)
 * Useful for UI rendering when you've already pre-fetched profiles
 */
export function getProfileFromCache(userId: string): UserProfile | null {
  if (!userId) return null;

  const now = Date.now();
  const cached = profileCache.get(userId);

  if (cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached.profile;
  }

  return null;
}

/**
 * Clear the entire cache (useful on logout)
 */
export function clearProfileCache(): void {
  profileCache.clear();
  pendingFetches.clear();
  clearPersistedProfiles();
  console.log('🧹 ProfileCache: Cache cleared');
}

/**
 * Clear a specific user from cache (useful when profile is updated)
 */
export function invalidateProfile(userId: string): void {
  profileCache.delete(userId);
  removePersistedProfile(userId);
  console.log(`🧹 ProfileCache: Invalidated cache for ${userId}`);
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats(): { size: number; pendingFetches: number } {
  return {
    size: profileCache.size,
    pendingFetches: pendingFetches.size
  };
}
